<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { BarChart3, Bot, Boxes, LayoutDashboard, Menu, RefreshCw, Settings, LogOut } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useAppDataStore } from '@/stores/app-data'
import { useUiStore } from '@/stores/ui'

const route = useRoute()
const auth = useAuthStore()
const data = useAppDataStore()
const ui = useUiStore()
const mobileOpen = ref(false)
const refreshing = ref(false)
const title = computed(() => String(route.meta.title ?? '工作台'))

const navItems = [
  { to: '/dashboard', label: '每日数据', icon: LayoutDashboard },
  { to: '/reports', label: '营业报表', icon: BarChart3 },
  { to: '/inventory', label: '库存管理', icon: Boxes },
  { to: '/assistant', label: 'AI 助手', icon: Bot, soon: true },
  { to: '/master-data', label: '基本信息', icon: Settings }
]

async function refresh() {
  refreshing.value = true
  try {
    await data.load(true)
    ui.toast('服务器数据已刷新')
  } catch (error) {
    ui.toast(error instanceof Error ? error.message : '刷新失败', 'error')
  } finally {
    refreshing.value = false
  }
}

function handleVisibility() {
  if (!document.hidden) void data.load(true)
}

onMounted(() => document.addEventListener('visibilitychange', handleVisibility))
onUnmounted(() => document.removeEventListener('visibilitychange', handleVisibility))
</script>

<template>
  <div class="app-shell">
    <aside class="sidebar" :class="{ 'mobile-open': mobileOpen }">
      <div class="sidebar-head">
        <div class="brand-mark">闫</div>
        <div class="brand-copy"><strong>闫老大管理系统</strong><span>老板工作台</span></div>
      </div>
      <nav class="nav" aria-label="主导航">
        <RouterLink v-for="item in navItems" :key="item.to" :to="item.to" class="nav-link" @click="mobileOpen = false">
          <component :is="item.icon" />
          <span class="nav-label">{{ item.label }}</span>
          <span v-if="item.soon" class="soon">V2.0</span>
        </RouterLink>
      </nav>
      <div class="sidebar-foot">
        <div class="user-chip">
          <div class="avatar">{{ auth.user?.name?.slice(0, 1) || '老' }}</div>
          <div class="user-copy"><strong>{{ auth.user?.name || '老板' }}</strong><span>超级管理员</span></div>
        </div>
      </div>
    </aside>

    <div class="main-shell">
      <header class="topbar">
        <div class="topbar-left">
          <button class="icon-btn mobile-nav-toggle" type="button" title="打开导航" aria-label="打开导航" @click="mobileOpen = !mobileOpen"><Menu /></button>
          <div class="breadcrumb"><span>闫老大管理系统&nbsp; / &nbsp;</span><b>{{ title }}</b></div>
        </div>
        <div class="top-actions">
          <button class="icon-btn" type="button" title="刷新服务器数据" aria-label="刷新服务器数据" :disabled="refreshing" @click="refresh"><RefreshCw :class="{ spinning: refreshing }" /></button>
          <button class="btn btn-ghost" type="button" @click="auth.logout"><LogOut />退出登录</button>
        </div>
      </header>
      <main class="content"><RouterView /></main>
    </div>
  </div>
</template>

<style scoped>
.spinning { animation: spin .8s linear infinite; }
</style>
